/**
 * Client Pharmacy Vuex module for Portal Application
 * 
 * @module ClientPharmacy
 * @see
 * @author Bailey Kulman (Fresche Solutions)
 * @date June 2023
 */

import portalapi from "@services/portalapi";
import { resetStateData } from "@/utils/vuexStoreUtils";
import { getField, updateField } from "vuex-map-fields";

const DEFAULT_STATE = {
  pharmacyData: {},
  todaysSubmittedOrders: 0,
  customerGroups: [],
};

const state = structuredClone(DEFAULT_STATE);

const getters = {
  getField,

  getPharmacyData(st) {
    return st.pharmacyData;
  },

  getCustomerGroups(st) {
    return st.customerGroups;
  },

  getTodaysSubmittedOrders(st) {
    return st.todaysSubmittedOrders;
  },
};

const mutations = {
  updateField, // for vuex-map-fields

  async clearStateData(st) {
    st = resetStateData(st, DEFAULT_STATE);
  },

  setPharmacyData(st, payload) {
    st.pharmacyData = payload;
  },

  setCustomerGroups(st, payload) {
    st.customerGroups = payload;
  },

  setTodaysSubmittedOrders(st, payload) {
    st.todaysSubmittedOrders = payload;
  },
};

const actions = {
  // Get pharmacy data
  async fetchPharmacyData({ commit }, userStoreNum) 
  {
    let response = await portalapi.moduleClientPharmacy.getClientPharmacy(userStoreNum);

    if (response && response.status === vm.config.globalProperties.$constants.HTTP_STATUS_CODES.OK) {
      commit("setPharmacyData", response.data);
    }
  },


  // Update Site Settings
  async updateSiteSettings({ commit }, settings)
  {
    let response = await portalapi.moduleClientPharmacy.updateSiteSettings(settings.storeId, settings.data);
    
    if (response) {
      commit("setPharmacyData", settings.data);
    }
    
    return response;
  },
  

  // Get customer groups
  async fetchCustomerGroups({ commit }) 
  {
    const response = await portalapi.moduleClientPharmacy.getCustomerGroups();

    if (response && response.status === vm.config.globalProperties.$constants.HTTP_STATUS_CODES.OK) {
      await commit("setCustomerGroups", response.data);
    }
  },


  // Get all pharmacies data (Super Admin only)
  async getAllPharmacies({ commit }) 
  {
    let response = await portalapi.moduleAdmin.getAllPharmacies();

    if (response && response.status === vm.config.globalProperties.$constants.HTTP_STATUS_CODES.OK) {
      return response.data;
    }
  },


  // Get all unregistered pharmacies data (Super Admin only)
  async getAllUnregisteredPharmacies({ commit }) 
  {
    let response = await portalapi.moduleAdmin.getAllUnregisteredPharmacies();

    if (response && response.status === vm.config.globalProperties.$constants.HTTP_STATUS_CODES.OK) {
      return response.data;
    }
  },


  // Create a new pharmacy (Super Admin only)
  async createClientPharmacy({ commit }, accountNumber) 
  {
    let response = await portalapi.moduleAdmin.createClientPharmacy(accountNumber);

    if (response && response.status === vm.config.globalProperties.$constants.HTTP_STATUS_CODES.CREATED) {
      return response;
    } else {
      return response.response;
    }
  },


  // Fetch today's submitted orders
  async fetchTodaysSubmittedOrders({ commit }) 
  {
    let response = await portalapi.moduleClientPharmacy.getDailyOrderTotal();

    if (response && response.status === vm.config.globalProperties.$constants.HTTP_STATUS_CODES.OK) {
      commit("setTodaysSubmittedOrders", response.data);
    } else {
      return response;
    }
  },
};

export default {
  namespaced: "true",
  state,
  getters,
  mutations,
  actions,
};
